import { Honeybadger, HoneybadgerErrorBoundary } from '@honeybadger-io/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { configureApiClient } from '@transreport/pauk-rrms-api'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AuthProvider } from 'react-oidc-context'

import { AppToaster } from '@/components/framework/AppToaster'
import { initializeContentsquare, initializeGoogleAnalytics } from '@/lib/analytics'
import { oidcConfig } from '@/lib/auth'
import '@/lib/i18n'
import { queryClient, setApiErrorReporter } from '@/lib/queryClient'

import { router } from './router'

import './index.css'

if (import.meta.env.MODE === 'a11y') {
  const { startBrowserMocks } = await import('@/tests/setup/msw.browser')
  await startBrowserMocks()
}

const googleAnalyticsMeasurementId = import.meta.env.VITE_APP_GA_MEASUREMENT_ID?.trim()
const contentsquareTagId = import.meta.env.VITE_APP_CONTENTSQUARE_TAG_ID?.trim()

if (googleAnalyticsMeasurementId) {
  initializeGoogleAnalytics(googleAnalyticsMeasurementId)
}

if (contentsquareTagId) {
  initializeContentsquare(contentsquareTagId)
}

const honeybadger = Honeybadger.configure({
  apiKey: import.meta.env.VITE_APP_HONEYBADGER_API_KEY,
  environment: import.meta.env.MODE,
  revision: import.meta.env.VITE_APP_COMMIT_HASH || 'dev',
})

setApiErrorReporter(({ source, key, status, url, error }) => {
  const fallbackError = new Error(`RRMS API ${source} failed with status ${status}`)
  const errorToReport = error instanceof Error ? error : fallbackError

  Honeybadger.notify(errorToReport, {
    context: {
      source,
      key,
      status,
      url,
    },
  })
})

configureApiClient({
  baseUrl: import.meta.env.VITE_APP_API_ENDPOINT,
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HoneybadgerErrorBoundary honeybadger={honeybadger}>
      <AuthProvider {...oidcConfig}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <AppToaster />
        </QueryClientProvider>
      </AuthProvider>
    </HoneybadgerErrorBoundary>
  </StrictMode>
)
